import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { SnackbarProvider } from 'notistack';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/common/ProtectedRoute';
import MainLayout from './components/layout/MainLayout';
import Login from './pages/auth/Login';
import Dashboard from './pages/dashboard/Dashboard';
import AdminList from './pages/admins/AdminList';
import EmployeeList from './pages/employees/EmployeeList';
import EmployerList from './pages/employers/EmployerList';
import JobList from './pages/jobs/JobList';
import CouponList from './pages/coupons/CouponList';
import CouponDetails from './pages/coupons/CouponDetails';
import PendingApprovals from './pages/coupons/PendingApprovals';
import CommissionList from './pages/commissions/CommissionList';
import CVRequestList from './pages/cv-requests/CVRequestList';
import ProfilePhotoApproval from './pages/profile-photos/ProfilePhotoApproval';
import PlanList from './pages/plans/PlanList';
import CatalogList from './pages/catalogs/CatalogList';
import OrderList from './pages/orders/OrderList';
import AdminDiagnostic from './pages/admins/AdminDiagnostic';
import AiLogList from './pages/ai/AiLogList';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2'
    },
    secondary: {
      main: '#dc004e'
    }
  }
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SnackbarProvider maxSnack={3} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <AuthProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <MainLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route
                  path="admins"
                  element={
                    <ProtectedRoute roles={['super_admin', 'manager']}>
                      <AdminList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admins/diagnostic"
                  element={
                    <ProtectedRoute>
                      <AdminDiagnostic />
                    </ProtectedRoute>
                  }
                />
                <Route path="employees" element={<EmployeeList />} />
                <Route path="employers" element={<EmployerList />} />
                <Route
                  path="jobs"
                  element={
                    <ProtectedRoute roles="super_admin">
                      <JobList />
                    </ProtectedRoute>
                  }
                />
                <Route path="coupons" element={<CouponList />} />
                <Route
                  path="coupons/pending"
                  element={
                    <ProtectedRoute roles="super_admin">
                      <PendingApprovals />
                    </ProtectedRoute>
                  }
                />
                <Route path="coupons/:id" element={<CouponDetails />} />
                <Route path="commissions" element={<CommissionList />} />
                <Route
                  path="cv-requests"
                  element={
                    <ProtectedRoute roles="super_admin">
                      <CVRequestList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="profile-photos"
                  element={
                    <ProtectedRoute roles="super_admin">
                      <ProfilePhotoApproval />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="plans"
                  element={
                    <ProtectedRoute roles="super_admin">
                      <PlanList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="catalogs"
                  element={
                    <ProtectedRoute roles={['super_admin', 'manager']}>
                      <CatalogList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="orders"
                  element={
                    <ProtectedRoute roles={['super_admin', 'manager', 'staff']}>
                      <OrderList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="ai-logs"
                  element={
                    <ProtectedRoute roles="super_admin">
                      <AiLogList />
                    </ProtectedRoute>
                  }
                />
              </Route>
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </SnackbarProvider>
    </ThemeProvider>
  );
}

export default App;
